import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Control, FieldValues, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CheckoutFormProps,
  DefaultFormActions,
  Form,
  useEnhancedForm,
} from '../../apiForm/form';
import ExpandableFieldset from '../../apiForm/form/ExpandableFieldset';
import {
  NumberField,
  SelectField,
  SelectOptionsField,
  TextField,
} from '../../apiForm/form/input';
import ArrayWrapper from '../../apiForm/wrapper/ArrayWrapper';
import { CustomerPropertiesLookupTypes } from '../../project/CheckoutConfig';

interface CustomerPropertiesLookupsProps {
  namePrefix: string;
  control: Control;
  readonly: boolean;
  watch: UseFormWatch<FieldValues>;
  serviceCredentials: any;
}

function CustomerPropertiesLookups({
  namePrefix,
  control,
  readonly,
  watch,
  serviceCredentials,
}: CustomerPropertiesLookupsProps) {
  const { t } = useTranslation();
  const selectedType = watch(`${namePrefix}.type`);
  const serviceCredentialsHelperText =
    serviceCredentials.length === 0
      ? t('checkoutConfig.helperText.general.noServiceCredentials')
      : t(
          'checkoutConfig.helperText.customerPropertiesLookups.serviceCredentialID',
        );

  return (
    <Stack spacing={2}>
      <SelectField
        name={`${namePrefix}.type`}
        control={control}
        disabled={readonly}
        label={t('checkoutConfig.label.customerPropertiesLookups.type')}
      >
        {Object.values(CustomerPropertiesLookupTypes).map((type) => (
          <MenuItem key={type} value={type}>
            {type
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (char) => char.toUpperCase())}
          </MenuItem>
        ))}
      </SelectField>
      <SelectOptionsField
        control={control}
        name={`${namePrefix}.serviceCredentialID`}
        disabled={readonly || serviceCredentials.length === 0}
        options={serviceCredentials}
        valueFrom="id"
        labelFrom="id"
        label={t(
          'checkoutConfig.label.customerPropertiesLookups.serviceCredentialID',
        )}
        helperText={serviceCredentialsHelperText}
      />
      {selectedType ===
        CustomerPropertiesLookupTypes.MULTIPLE_BOARDING_PASSES_LOOKUP && (
        <>
          <Typography variant="overline">
            {t('checkoutConfig.heading.boardingPassLookupConfig.general')}
          </Typography>
          <TextField
            control={control}
            name="boardingPassLookupConfig.airportCode"
            label={t(
              'checkoutConfig.label.boardingPassLookupConfig.airportCode',
            )}
            disabled={readonly}
          />
          <TextField
            control={control}
            name="boardingPassLookupConfig.dummyBoardingPass.airline"
            label={t(
              'checkoutConfig.label.boardingPassLookupConfig.dummyBoardingPass.airline',
            )}
            disabled={readonly}
          />
          <TextField
            control={control}
            name="boardingPassLookupConfig.dummyBoardingPass.flightNumber"
            label={t(
              'checkoutConfig.label.boardingPassLookupConfig.dummyBoardingPass.flightNumber',
            )}
            disabled={readonly}
          />
          <SelectField
            control={control}
            name="boardingPassLookupConfig.dummyBoardingPass.customID"
            label={t(
              'checkoutConfig.label.boardingPassLookupConfig.dummyBoardingPass.customID',
            )}
            disabled={readonly}
          >
            <MenuItem key="D" value="D">
              D
            </MenuItem>
            <MenuItem key="U" value="U">
              U
            </MenuItem>
            <MenuItem key="S" value="S">
              S
            </MenuItem>
            <MenuItem key="X" value="X">
              X
            </MenuItem>
          </SelectField>
          <Divider />
          <Typography variant="overline">
            {t('checkoutConfig.heading.boardingPassLookupConfig.shops')}
          </Typography>
          <ArrayWrapper
            control={control}
            name="boardingPassLookupConfig.shops"
            defaultValue={{}}
            addLabel={t(
              'checkoutConfig.arrayWrapper.boardingPassLookupConfig.shop.add',
            )}
            removeLabel={t(
              'checkoutConfig.arrayWrapper.boardingPassLookupConfig.shop.remove',
            )}
            render={(innerIndex: number) => (
              <Stack spacing={2}>
                <TextField
                  control={control}
                  name={`boardingPassLookupConfig.shops[${innerIndex}].id`}
                  label={t(
                    'checkoutConfig.label.boardingPassLookupConfig.shops.id',
                  )}
                  disabled={readonly}
                />
                <SelectField
                  control={control}
                  name={`boardingPassLookupConfig.shops[${innerIndex}].allowedFlightDirections`}
                  label={t(
                    'checkoutConfig.label.boardingPassLookupConfig.shops.allowedFlightDirections',
                  )}
                  disabled={readonly}
                >
                  <MenuItem key="arrival" value="arrival">
                    arrival
                  </MenuItem>
                  <MenuItem key="departure" value="departure">
                    departure
                  </MenuItem>
                  <MenuItem key="both" value="both">
                    both
                  </MenuItem>
                </SelectField>
                <NumberField
                  control={control}
                  name={`boardingPassLookupConfig.shops[${innerIndex}].validDateOffsetHours`}
                  label={t(
                    'checkoutConfig.label.boardingPassLookupConfig.shops.validDateOffsetHours',
                  )}
                  disabled={readonly}
                />
                <NumberField
                  control={control}
                  name={`boardingPassLookupConfig.shops[${innerIndex}].maxStopoverHours`}
                  label={t(
                    'checkoutConfig.label.boardingPassLookupConfig.shops.maxStopoverHours',
                  )}
                  disabled={readonly}
                />
                <TextField
                  control={control}
                  name={`boardingPassLookupConfig.shops[${innerIndex}].blockedCustomsIds`}
                  label={t(
                    'checkoutConfig.label.boardingPassLookupConfig.shops.blockedCustomsIds',
                  )}
                  disabled={readonly}
                />
              </Stack>
            )}
          />
        </>
      )}
    </Stack>
  );
}

function mapBoardingPassLookupConfigShopsToArray(shops: any): any {
  const boardingPassLookupConfigShopsRaw = shops || {};
  const boardingPassLookupConfigShops = Object.entries(
    boardingPassLookupConfigShopsRaw,
  ).map(([key, value]) => ({ ...value, id: key }));
  return boardingPassLookupConfigShops;
}

export default function CustomerPropertiesLookupForm({
  defaultValues = {},
  errors = {},
  onSubmit,
  readonly,
}: CheckoutFormProps<any>) {
  const { t } = useTranslation();

  const normalizedDefaults = {
    ...defaultValues,
    boardingPassLookupConfig: defaultValues.boardingPassLookupConfig
      ? {
          ...defaultValues.boardingPassLookupConfig,
          shops: mapBoardingPassLookupConfigShopsToArray(
            defaultValues.boardingPassLookupConfig?.shops,
          ),
        }
      : undefined,
  };

  const { handleSubmit, control, formState, getValues, watch, reset } =
    useEnhancedForm({ defaultValues: normalizedDefaults, errors });

  const serviceCredentials = getValues('serviceCredentials') || [];

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ExpandableFieldset
        legend={t('checkoutConfig.legend.customerPropertiesLookups')}
      >
        <ArrayWrapper
          control={control}
          name="customerPropertiesLookups"
          defaultValue={{}}
          addLabel={t(
            'checkoutConfig.arrayWrapper.customerPropertiesLookup.add',
          )}
          removeLabel={t(
            'checkoutConfig.arrayWrapper.customerPropertiesLookup.remove',
          )}
          render={(_: number, namePrefix: string) => (
            <CustomerPropertiesLookups
              namePrefix={namePrefix}
              control={control}
              readonly={!!readonly}
              watch={watch}
              serviceCredentials={serviceCredentials}
            />
          )}
        />
      </ExpandableFieldset>

      <DefaultFormActions
        formState={formState}
        cancelNavigationTarget="/"
        noCancelNavigation
        reset={reset}
        getValues={getValues}
      />
    </Form>
  );
}
